import { getDateKey } from '~/helpers'
import { OpeningHoursStatusType, type OpeningHoursResponse } from '../types'

type OpeningHoursStatus = {
  /**
   * Whether the node is open at this moment.
   *
   * This value may be used to directly inform the user about the status. e.g.
   * `true` means that they can visit this location at this exact moment.
   */
  isOpen: boolean

  /**
   * In how many minutes the location will open.
   */
  opensInMinutes?: number

  /**
   * In how many minutes the location will close.
   */
  closesInMinutes?: number

  /**
   * The internal, detailled status type.
   */
  statusType?: OpeningHoursStatusType | null
}

type OpeningHoursStatusLabel = { label: string; additional?: string | null }

type UseOpeningHours = {
  /**
   * Get the opening hours status for the given Node ID.
   *
   * If no opening hours exist it will return null.
   * If opening hours exist it will return the current opening hours status.
   */
  getStatus: (nodeId: string) => OpeningHoursStatus | null

  /**
   * Get the opening hours status labels.
   */
  getStatusLabel: (
    status?: OpeningHoursStatus | null,
  ) => OpeningHoursStatusLabel | null

  /**
   * Load the opening hours.
   */
  load: () => Promise<void>
}

type State = {
  /**
   * Whether the opening hours have already been fetched.
   */
  fetched: boolean

  /**
   * The fetched opening hours.
   */
  openingHours: OpeningHoursResponse
}

/**
 * Provides methods to load and determine the opening hours status for nodes.
 */
export function useOpeningHours(): UseOpeningHours {
  const openingHours = useState<State>('openingHours', () => {
    return {
      fetched: false,
      openingHours: {},
    }
  })

  const date = new Date()
  const now = computed(() => Math.round(date.getTime() / 1000))

  async function load() {
    if (import.meta.client && !openingHours.value.fetched) {
      openingHours.value.fetched = true
      const dateKey = getDateKey(date)
      try {
        openingHours.value.openingHours = await $fetch<OpeningHoursResponse>(
          '/api/opening-hours',
          {
            params: {
              date: dateKey,
            },
          },
        )
      } catch {
        // Noop if this request fails.
      }
    }
  }

  function getStatus(nodeId: string) {
    const status = openingHours.value.openingHours[nodeId]

    const statusType = typeof status === 'number' ? status : null

    // Handle missing nid in opening hours map. This implies there is no
    // information available about the opening hours. The status should not
    // be shown.
    if (status === null || status === undefined) {
      return null
    }

    // If the status is a boolean it either means it's marked as "temporarily
    // closed" (false) or "always (24/7) open" (true).
    if (
      status === OpeningHoursStatusType.CurrentlyClosed ||
      status === OpeningHoursStatusType.SeasonallyClosed
    ) {
      return {
        isOpen: false,
        statusType,
      }
    }

    if (status === OpeningHoursStatusType.AlwaysOpen) {
      return {
        isOpen: true,
        statusType,
      }
    }

    // The result is an array and we have to iterate over it to see if it is
    // currently open.
    let secondsUntilOpen = 0

    if (Array.isArray(status)) {
      for (let i = 0; i < status.length; i++) {
        const from = status[i][0]
        const to = status[i][1]
        if (now.value >= from && now.value < to) {
          return {
            isOpen: true,
            closesInMinutes: Math.round((to - now.value) / 60),
            statusType,
          }
        }

        const nextEntry = status[i + 1]
        if (nextEntry) {
          const start = nextEntry[0]
          if (!secondsUntilOpen || start - now.value < secondsUntilOpen) {
            secondsUntilOpen = start - now.value
          }
        }
      }
    }

    return {
      isOpen: false,
      opensInMinutes: Math.round(secondsUntilOpen / 60),
      statusType,
    }
  }

  const { $texts } = useEasyTexts()

  function getStatusLabelAdditional(
    status?: OpeningHoursStatus,
  ): string | null {
    if (!status) {
      return null
    }

    if (status.opensInMinutes && status.opensInMinutes < 30) {
      const text =
        status.opensInMinutes === 1
          ? $texts('openingHoursOpensInSingle', 'Opens in @count minute')
          : $texts('openingHoursOpensIn', 'Opens in @count minutes')
      return text.replace('@count', String(status.opensInMinutes))
    } else if (status.closesInMinutes && status.closesInMinutes < 30) {
      const text =
        status.closesInMinutes === 1
          ? $texts('openingHoursClosesInSingle', 'Closes in @count minute')
          : $texts('openingHoursClosesIn', 'Closes in @count minutes')
      return text.replace('@count', String(status.closesInMinutes))
    }

    return null
  }

  function getStatusLabelBase(status: OpeningHoursStatus): string {
    if (
      status.isOpen &&
      status.statusType === OpeningHoursStatusType.AlwaysOpen
    ) {
      return $texts('openingHoursAlwaysOpen', 'Always open')
    }

    return status.isOpen
      ? $texts('openingHoursOpen', 'Open')
      : $texts('openingHoursClosed', 'Closed')
  }

  function getStatusLabel(
    status?: OpeningHoursStatus | null,
  ): OpeningHoursStatusLabel | null {
    if (!status) {
      return null
    }

    return {
      label: getStatusLabelBase(status),
      additional: getStatusLabelAdditional(status),
    }
  }

  return {
    load,
    getStatus,
    getStatusLabel,
  }
}
